// src/pages/creator-panel/CallLogs/CallDetailDialog.js
// Large MUI Dialog opening a single call's transcripts / logs / analysis.
import React, { useEffect, useState, useCallback } from 'react';
import {
    Dialog, DialogContent, Box, Typography, IconButton, Tooltip, Tab, Tabs,
    CircularProgress, Snackbar, Alert, Chip, Stack,
} from '@mui/material';
import {
    Close as CloseIcon,
    KeyboardArrowUp as UpIcon,
    KeyboardArrowDown as DownIcon,
    ContentCopy as CopyIcon,
    Phone as PhoneIcon,
    AccessTime as ClockIcon,
    AttachMoney as DollarIcon,
    Forum as ForumIcon,
    ViewList as ListIcon,
    Analytics as AnalyticsIcon,
    Code as CodeIcon,
    PieChart as PieChartIcon,
    Storage as StorageIcon,
    Hub as HubIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { formatHeaderDate, formatDuration, formatCost, truncateId } from './utils/formatters';
import TranscriptsTab from './tabs/TranscriptsTab';
import LogsTab from './tabs/LogsTab';
import RagLogTab from './tabs/RagLogTab';
import AnalysisTab from './tabs/AnalysisTab';
import MessagesTab from './tabs/MessagesTab';
import CallCostTab from './tabs/CallCostTab';
import WaveformPlayer from './components/WaveformPlayer';

const TABS = [
    { key: 'transcripts', label: 'Transcripts', icon: <ForumIcon fontSize="small" /> },
    { key: 'logs', label: 'Logs', icon: <ListIcon fontSize="small" /> },
    // Vector DB + Entity tabs are RAG-only — hidden when the chatbot's
    // knowledgebase is disabled (see visibleTabs below).
    { key: 'vectordb', label: 'Vector DB', icon: <StorageIcon fontSize="small" />, ragOnly: true },
    { key: 'entity', label: 'Entity', icon: <HubIcon fontSize="small" />, ragOnly: true },
    { key: 'analysis', label: 'Analysis', icon: <AnalyticsIcon fontSize="small" /> },
    { key: 'messages', label: 'Messages', icon: <CodeIcon fontSize="small" /> },
    { key: 'callcost', label: 'Call Cost', icon: <PieChartIcon fontSize="small" /> },
];

const CallDetailDialog = ({
    open, onClose,
    sessionId,                  // id of the currently open call
    filteredCalls,              // ordered list from parent — for prev/next nav
    onNavigateToSession,        // (id) => void, called when prev/next changes target
    onEdgeNavigate,             // (dir: 'prev'|'next') => Promise<boolean>, fetches adjacent page
    pageInfo,                   // { page, totalPages } — used to detect dataset boundaries
}) => {
    const [active, setActive] = useState('transcripts');
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);

    // Audio-URL state lives at the dialog level so the WaveformPlayer can
    // render outside the tab panel and persist across tab switches.
    const [audioUrl, setAudioUrl] = useState('');
    const [audioLoading, setAudioLoading] = useState(false);
    const [audioError, setAudioError] = useState(false);

    useEffect(() => {
        if (!open || !sessionId) return;
        let mounted = true;
        setLoading(true);
        setError('');
        setSession(null);
        setAudioUrl('');
        setAudioError(false);
        axiosInstance.get(`/api/call-sessions/${sessionId}`)
            .then(res => {
                if (!mounted) return;
                setSession(res?.data?.data || null);
            })
            .catch(() => {
                if (!mounted) return;
                setError('Could not load call details.');
            })
            .finally(() => mounted && setLoading(false));
        return () => { mounted = false; };
    }, [open, sessionId]);

    // Eagerly request the signed audio URL once the session loads so the
    // play button is ready without an extra click.
    const fetchAudioUrl = useCallback(async () => {
        if (!session?.id || !session?.recording_id || audioUrl) return;
        setAudioLoading(true);
        setAudioError(false);
        try {
            const res = await axiosInstance.get(`/api/call-sessions/${session.id}/audio-url`);
            const url = res?.data?.data?.url;
            if (url) setAudioUrl(url);
            else setAudioError(true);
        } catch (e) {
            setAudioError(true);
        } finally {
            setAudioLoading(false);
        }
    }, [session?.id, session?.recording_id, audioUrl]);

    useEffect(() => {
        if (session?.id && session?.recording_id) fetchAudioUrl();
    }, [session?.id, session?.recording_id, fetchAudioUrl]);

    // If the active tab isn't available for the loaded session — e.g. the
    // RAG-only Vector DB / Entity tabs when this session's knowledgebase is
    // disabled (can happen after prev/next navigation, since `active` persists)
    // — fall back to Transcripts so MUI's Tabs value stays in range.
    useEffect(() => {
        if (!session) return;
        const tab = TABS.find(t => t.key === active);
        if (tab?.ragOnly && !session.rag_enabled) setActive('transcripts');
    }, [session, active]);

    // ESC to close
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    const currentIndex = filteredCalls?.findIndex(c => c.id === sessionId) ?? -1;
    const lastIndex = (filteredCalls?.length || 0) - 1;
    const atFirstPage = !pageInfo || pageInfo.page <= 1;
    const atLastPage = !pageInfo || pageInfo.page >= (pageInfo.totalPages || 1);

    const handlePrev = useCallback(() => {
        if (currentIndex > 0) {
            onNavigateToSession(filteredCalls[currentIndex - 1].id);
        } else if (!atFirstPage && onEdgeNavigate) {
            // Boundary: fetch the previous page and open its last row.
            onEdgeNavigate('prev');
        }
    }, [currentIndex, filteredCalls, onNavigateToSession, onEdgeNavigate, atFirstPage]);

    const handleNext = useCallback(() => {
        if (currentIndex >= 0 && currentIndex < lastIndex) {
            onNavigateToSession(filteredCalls[currentIndex + 1].id);
        } else if (!atLastPage && onEdgeNavigate) {
            // Boundary: fetch the next page and open its first row.
            onEdgeNavigate('next');
        }
    }, [currentIndex, lastIndex, filteredCalls, onNavigateToSession, onEdgeNavigate, atLastPage]);

    const prevDisabled = currentIndex <= 0 && atFirstPage;
    const nextDisabled = (currentIndex < 0 || currentIndex >= lastIndex) && atLastPage;

    const handleCopyId = async () => {
        if (!session?.session_uid) return;
        try {
            await navigator.clipboard.writeText(session.session_uid);
            setCopied(true);
        } catch (e) { /* clipboard blocked */ }
    };

    const renderHeader = () => {
        if (!session) return null;
        return (
            <Box sx={{
                position: 'sticky', top: 0, zIndex: 2,
                bgcolor: 'background.paper',
                borderBottom: '1px solid', borderColor: 'divider',
                px: 3, py: 2,
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
                            <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                {formatHeaderDate(session.started_at)} {session.direction === 'Outbound' ? 'outboundPhoneCall' : 'inboundPhoneCall'}
                            </Typography>
                            <Chip size="small" label="Assistant" variant="outlined" sx={{ height: 22, fontWeight: 600 }} />
                        </Stack>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: 'text.secondary', mb: 0.5, flexWrap: 'wrap' }}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <PhoneIcon sx={{ fontSize: 14 }} />
                                <Typography variant="caption">
                                    Customer: {session.customer_phone_raw || '—'}
                                    {session.customer_phone_formatted && session.customer_phone_formatted !== session.customer_phone_raw
                                        ? ` • ${session.customer_phone_formatted}` : ''}
                                </Typography>
                            </Stack>
                            {session.ended_reason && (
                                <Typography variant="caption">Ended: {session.ended_reason}</Typography>
                            )}
                        </Box>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.disabled' }}>
                                {truncateId(session.session_uid, 8, 4) || '—'}
                            </Typography>
                            <Tooltip title={copied ? 'Copied' : 'Copy full ID'}>
                                <IconButton size="small" onClick={handleCopyId} sx={{ p: 0.5 }}>
                                    <CopyIcon fontSize="inherit" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mr: 1 }}>
                                <DollarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                    Cost: {formatCost(session.cost_usd)}
                                </Typography>
                            </Stack>
                            <Tooltip title="Previous call">
                                <span>
                                    <IconButton size="small" onClick={handlePrev} disabled={prevDisabled}>
                                        <UpIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Next call">
                                <span>
                                    <IconButton size="small" onClick={handleNext} disabled={nextDisabled}>
                                        <DownIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <IconButton size="small" onClick={onClose}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <ClockIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                                Duration: {formatDuration(session.duration_seconds)}
                            </Typography>
                        </Stack>
                    </Box>
                </Box>
            </Box>
        );
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { minHeight: '80vh' } }}
            >
                {renderHeader()}
                <DialogContent sx={{ p: 3 }}>
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {error && !loading && (
                        <Alert severity="error">{error}</Alert>
                    )}
                    {!loading && !error && session && (
                        <>
                            {/* Recording sits above the tabs so it stays visible regardless of which tab is active.
                                Text Chat is text-only — no audio is ever produced — so hide the player entirely
                                for that channel (Recording / phone / live-conversation all keep it). */}
                            {session?.channel !== 'text-chat' && (
                                <Box sx={{ mb: 2 }}>
                                    <WaveformPlayer
                                        audioUrl={audioUrl}
                                        audioUrlLoading={audioLoading}
                                        audioUrlError={audioError || !session?.recording_id}
                                        durationSeconds={session?.duration_seconds || 0}
                                        peaks={session?.waveform_peaks}
                                        onRequestAudioUrl={fetchAudioUrl}
                                    />
                                </Box>
                            )}

                            <Tabs
                                value={active}
                                onChange={(_, v) => setActive(v)}
                                indicatorColor="primary"
                                textColor="primary"
                                sx={{
                                    minHeight: 40,
                                    borderBottom: '1px solid', borderColor: 'divider',
                                    mb: 2,
                                }}
                            >
                                {TABS
                                    .filter(t => !t.ragOnly || session?.rag_enabled)
                                    .map(t => (
                                        <Tab
                                            key={t.key}
                                            value={t.key}
                                            icon={t.icon}
                                            iconPosition="start"
                                            label={t.label}
                                            sx={{ minHeight: 40, py: 0, fontSize: 13 }}
                                        />
                                    ))}
                            </Tabs>

                            {active === 'transcripts' && <TranscriptsTab session={session} />}
                            {active === 'logs' && <LogsTab logs={session.logs || []} />}
                            {active === 'vectordb' && session?.rag_enabled && (
                                <RagLogTab sessionId={session.id} logType="vectordb" label="Vector DB" />
                            )}
                            {active === 'entity' && session?.rag_enabled && (
                                <RagLogTab sessionId={session.id} logType="entity" label="Entity" />
                            )}
                            {active === 'analysis' && <AnalysisTab session={session} />}
                            {active === 'messages' && <MessagesTab session={session} />}
                            {active === 'callcost' && <CallCostTab session={session} />}
                        </>
                    )}
                </DialogContent>
            </Dialog>
            <Snackbar
                open={copied}
                autoHideDuration={1500}
                onClose={() => setCopied(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                message="Copied"
            />
        </>
    );
};

export default CallDetailDialog;
